import { Link, useNavigate } from "react-router-dom";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { useSurvey } from "../../contexts/Survey";
import JourneyBand, { JourneyScrim } from "../Decor/JourneyBand";
import HeadingWithInfo from "../HeadingWithInfo/HeadingWithInfo";
import SurveyInfo from "../Forms/SurveyWizard/SurveyInfo";
import { surveyTypeLabel } from "../Dashboard/labels";
import styles from "./SurveyHero.module.css";

type SurveyHeroProps = {
  isEdit: boolean;
  onToggleEdit: () => void;
};

const SurveyHero = ({ isEdit, onToggleEdit }: SurveyHeroProps) => {
  const t = useTranslations("surveys");
  const tAll = useTranslations("surveyForm");
  const d = useDateFormatter();
  const navigate = useNavigate();
  const { survey, isOwner, canEdit, isShared } = useSurvey();
  if (!survey) {
    return null;
  }
  const { id, surveyName, status, startDate, endDate, changed, respondents = 0, invited = 0, createdOnBehalf, createdBy, candidate } = survey;

  const createGoal = () => {
    navigate(`/improvement?fromSurvey=${id}`);
    window.scrollTo(0, 0);
  };

  return (
    <header className={styles.hero}>
      {/* Still while the owner fills in the edit form below. */}
      <JourneyBand curve='settle' wave={isEdit ? undefined : "settle"} />
      <JourneyScrim />
      <Link className={styles.back} to={isShared ? "/shared" : "/surveys"}>
        ← {isShared ? t("backToShared") : t("backToSurveys")}
      </Link>
      <p className={styles.badge}>
        {tAll(surveyTypeLabel(survey.surveyType))} · {t(`status_${status}`)}
      </p>
      <HeadingWithInfo className={styles.heading} level={1} heading={surveyName} info={<SurveyInfo surveyStatus={status} />} />
      <ul className={styles.facts}>
        <li>{t("start", d(startDate))}</li>
        <li>{t("end", d(endDate))}</li>
        <li>{t("respondents", respondents, Math.max(invited, respondents))}</li>
        <li>{t("changed", d(changed, true))}</li>
      </ul>
      {createdOnBehalf && (
        <p className={styles.meta}>
          {t("creator_onBehalfOf")} <strong>{createdBy}</strong> {t("creator_for")} <strong>{candidate}</strong>
        </p>
      )}
      <div className={styles.actions}>
        {isOwner && (
          <button type='button' className={styles.secondary} data-testid='survey-detail-edit-button' aria-pressed={isEdit} onClick={onToggleEdit}>
            {t("editSurvey")}
          </button>
        )}
        {status === "finished" && canEdit && (
          <a
            className={styles.primary}
            href={`/improvement?fromSurvey=${id}`}
            onClick={(event) => {
              event.preventDefault();
              createGoal();
            }}
          >
            {t("setImprovementGoal")}
          </a>
        )}
      </div>
    </header>
  );
};

export default SurveyHero;
